<script setup>
// app/javascript/dashboard/components/ImportContactsModal.vue
// app/javascript/dashboard/components/ImportContactsModal.vue
// app/javascript/dashboard/components/ImportContactsModal.vue
import { ref, reactive, computed, watch } from 'vue';
import Papa from 'papaparse';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';
import SuccessModal from '@/dashboard/components/SuccessModal.vue';
import CustomDataTable from '@/dashboard/components/CustomDataTable.vue';
import wootAPI from '@/v3/api/apiClient';

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close', 'import-contacts']);

const fileInput = ref(null);
const csvFile = ref(null);
const parsedCsvRows = ref([]);
const csvHeaders = ref([]);
const showMapper = ref(false);
const wizardStep = ref(0);
const initialScreen = ref(true);
const loading = ref(false);
const error = ref(null);
const successCount = ref(0);
const errorCount = ref(0);
const importResults = ref([]);
const showSuccessModal = ref(false);
const showImportModal = ref(true);
const importCancelled = ref(false);

// Import state
const duplicateCount = ref(0);

// Warning state for all-duplicates case
const showDuplicateWarning = computed(() => {
  return !loading.value && !error.value && duplicateCount.value > 0 && successCount.value === 0 && errorCount.value === 0;
});


// Para o popup de visualização de texto longo
const showFullTextModal = ref(false);
const fullTextContent = ref('');
const fullTextTitle = ref('');

// Column selection and hover state
const selectedColumn = ref('');
const hoveredColumn = ref('');

const mapping = reactive({
  name: '',
  phone_number: '',
  email: ''
});

const headerOptions = computed(() => {
  if (!csvHeaders.value) return [];
  return csvHeaders.value.map(header => ({
    value: header,
    label: header,
  }));
});

// Format and validate helpers
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return null;
  
  // Remove any non-numeric characters except + for international format
  let formatted = phoneNumber.toString().replace(/[^\d+]/g, '');
  
  // Ensure the phone number is valid (simple validation - can be improved)
  if (formatted.length < 5) return null;
  
  // Ensure the phone number starts with +
  if (!formatted.startsWith('+')) {
    formatted = '+' + formatted;
  }
  
  return formatted;
};

// Format WhatsApp identifier as "XXXXXXXXXX@s.whatsapp.net"
const formatWhatsAppIdentifier = (phoneNumber) => {
  if (!phoneNumber) return null;
  
  // Remove all non-numeric characters to get clean digits
  const digits = phoneNumber.toString().replace(/\D/g, '');
  
  // Ensure we have enough digits for a valid phone number
  if (digits.length < 5) return null;
  
  // Create WhatsApp identifier format
  return `${digits}@s.whatsapp.net`;
};

const validateEmail = (email) => {
  if (!email) return null;
  
  // Simple email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) ? email.trim() : null;
};

// Define resetMappings para usar no watcher
const resetMappings = () => {
  // Reset all mappings
  Object.keys(mapping).forEach(key => {
    mapping[key] = '';
  });
  
  parsedCsvRows.value = [];
  csvHeaders.value = [];
  error.value = null;
  importResults.value = [];
  importCancelled.value = false;
  
  // Reset import state
  duplicateCount.value = 0;
  
};

// Separate function to reset everything including counts (used only when fully closing)
const resetAll = () => {
  resetMappings();
  successCount.value = 0;
  errorCount.value = 0;
};

// Watch para a prop show, para resetar o estado quando o modal é fechado
watch(() => props.show, (newValue) => {
  if (!newValue && !showSuccessModal.value) {
    // Only reset if modal is closing AND we're not showing success modal
    // Cancel ongoing import when modal is closed
    importCancelled.value = true;
    // Reset states quando o modal é fechado
    initialScreen.value = true;
    showMapper.value = false;
    wizardStep.value = 0;
    resetAll(); // Reset everything including counts when modal is fully closed
  } else if (newValue) {
    showImportModal.value = true; // Garante que o modal apareça quando show é true
  }
}, { immediate: true });

// Watch for showImportModal changes to avoid resetting counts when hiding import modal to show success modal
watch(() => showImportModal.value, (newValue) => {
  // Don't reset counts when hiding import modal if we're about to show success modal
}, { immediate: false });

// State para o wizard
const wizardTitle = computed(() => {
  const titles = [
    'Nome do contato',
    'Número de telefone do contato',
    'Email do contato'
  ];
  return titles[wizardStep.value] || 'IMPORTAÇÃO';
});

const wizardDescription = computed(() => {
  const descriptions = [
    'Selecione a coluna que contém os nomes dos contatos. Este campo é obrigatório.',
    'Selecione a coluna que contém os números de telefone dos contatos. Este campo é obrigatório e deve conter números válidos.',
    'Selecione a coluna que contém os emails dos contatos. Este campo é opcional.'
  ];
  return descriptions[wizardStep.value] || '';
});

const canGoNext = computed(() => {
  if (wizardStep.value === 0) {
    return !!mapping.name; // Nome é obrigatório
  }
  if (wizardStep.value === 1) {
    return !!mapping.phone_number; // Número de telefone é obrigatório
  }
  return true; // Email é opcional
});

const isLastStep = computed(() => {
  return wizardStep.value === 2; // Último passo é "Email"
});


const successModalMessage = computed(() => {
  
  let message = `${successCount.value} contato(s) importado(s) com sucesso`;
  
  const skippedCount = importResults.value.filter(r => r.status === 'skipped').length;
  if (skippedCount > 0) {
    message += `, ${skippedCount} duplicado(s) ignorado(s)`;
  }
  
  if (errorCount.value > 0) {
    message += `, ${errorCount.value} falha(s)`;
  }
  
  message += '. Os contatos já estão disponíveis no sistema.';
  return message;
});

// Computed to sync selectedColumn with current step mapping
const currentStepSelectedColumn = computed(() => {
  if (wizardStep.value === 0) {
    return mapping.name;
  } else if (wizardStep.value === 1) {
    return mapping.phone_number;
  } else if (wizardStep.value === 2) {
    return mapping.email;
  }
  return '';
});

// Watch to update selectedColumn when wizard step changes
watch(wizardStep, () => {
  // Only set selectedColumn if there's already a mapping for this step
  if (currentStepSelectedColumn.value) {
    selectedColumn.value = currentStepSelectedColumn.value;
  } else {
    selectedColumn.value = '';
  }
}, { immediate: true });

// Functions auth
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

const getAuthHeaders = () => {
  const cookie = getCookie('cw_d_session_info');
  if (!cookie) {
    return {};
  }
  try {
    const sessionInfo = JSON.parse(cookie);
    return {
      'access-token': sessionInfo['access-token'],
      'client': sessionInfo['client'],
      'uid': sessionInfo['uid']
    };
  } catch (e) {
    return {};
  }
};


// Methods
const triggerFileSelect = () => {
  fileInput.value?.click();
};

const handleFileUpload = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  csvFile.value = file;

  // Read and parse the CSV file
  const reader = new FileReader();
  reader.onload = () => {
    const csvData = reader.result;
    Papa.parse(csvData, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        // guarda colunas e linhas brutas
        csvHeaders.value = results.meta.fields;
        parsedCsvRows.value = results.data;
        // inicia o wizard no primeiro passo
        wizardStep.value = 0;
        // muda para a tela de mapeamento
        initialScreen.value = false;
        showMapper.value = true;
        // Reset file input
        csvFile.value = null;
        event.target.value = null;
      }
    });
  };
  reader.readAsText(file);
};

const nextWizardStep = () => {
  if (wizardStep.value < 2) {
    wizardStep.value++;
    // Clear selection when moving to next step
    selectedColumn.value = '';
  } else {
    applyMapping();
  }
};

const previousWizardStep = () => {
  if (wizardStep.value > 0) {
    wizardStep.value--;
    // Clear selection when moving to previous step
    selectedColumn.value = '';
  }
};

const applyMapping = async () => {
  // Reset states
  error.value = null;
  loading.value = true;
  successCount.value = 0;
  errorCount.value = 0;
  importResults.value = [];
  importCancelled.value = false;
  
  // Get auth headers and verify user is logged in
  const headers = getAuthHeaders();
  if (!headers['access-token']) {
    error.value = 'Usuário não autenticado.';
    loading.value = false;
    return;
  }

  let mappedContacts = parsedCsvRows.value
    .map(row => {
      const contact = {};
      
      // Required fields
      if (!mapping.name || !row[mapping.name]) return null;
      contact.name = row[mapping.name].trim();
      
      if (!mapping.phone_number || !row[mapping.phone_number]) return null;
      const phoneNumber = formatPhoneNumber(row[mapping.phone_number]);
      if (phoneNumber === null) return null;
      contact.phone_number = phoneNumber;
      
      // Optional fields
      if (mapping.email && row[mapping.email]) {
        const email = validateEmail(row[mapping.email]);
        contact.email = email;
      }
      
      return contact;
    })
    .filter(Boolean);

  if (mappedContacts.length === 0) {
    error.value = 'Nenhum contato válido encontrado para importação.';
    loading.value = false;
    return;
  }

  const importedContacts = [];
  let csvDuplicateCount = 0;
  
  // Create AbortController for cancelling requests
  const abortController = new AbortController();
  
  // Update cancellation handler to abort HTTP requests
  const originalCancelled = importCancelled.value;
  const checkCancellation = () => {
    if (importCancelled.value && !originalCancelled) {
      abortController.abort();
      return true;
    }
    return false;
  };
  
  // Remove duplicates within the CSV data itself
  const seenPhones = new Set();
  const seenEmails = new Set();
  const originalCount = mappedContacts.length;
  
  mappedContacts = mappedContacts.filter(contact => {
    // Check for duplicate phone numbers
    if (seenPhones.has(contact.phone_number)) {
      csvDuplicateCount++;
      importResults.value.push({
        status: 'skipped',
        name: contact.name,
        message: 'Número de telefone duplicado no CSV'
      });
      return false;
    }
    
    // Check for duplicate emails (if email exists)
    if (contact.email && seenEmails.has(contact.email)) {
      csvDuplicateCount++;
      importResults.value.push({
        status: 'skipped',
        name: contact.name,
        message: 'Email duplicado no CSV'
      });
      return false;
    }
    
    // Add to seen sets
    seenPhones.add(contact.phone_number);
    if (contact.email) {
      seenEmails.add(contact.email);
    }
    
    return true;
  });
  
  
  if (mappedContacts.length === 0) {
    // All contacts are duplicates within CSV - show warning instead of error
    duplicateCount.value = originalCount;
    loading.value = false;
    // Don't set error, let the warning computed property handle this
    return;
  }
  
  // Prepare contacts for batch API request
  const contactsForBatch = mappedContacts.map(contact => ({
    name: contact.name,
    phone_number: contact.phone_number,
    email: contact.email || null,
    identifier: formatWhatsAppIdentifier(contact.phone_number),
    custom_attributes: {},
    additional_attributes: {}
  }));

  try {
    // Check if import was cancelled before making request
    if (checkCancellation()) {
      loading.value = false;
      return;
    }
    
    
    // Send all contacts in a single batch request
    const response = await wootAPI.post(
      `/api/v1/import/contact`,
      { contacts: contactsForBatch },
      { 
        headers,
        signal: abortController.signal
      }
    );
    
    // Process batch response
    const batchResult = response.data;
    
    if (batchResult.status === 'completed' && batchResult.summary) {
      const { summary } = batchResult;
      
      // Check if this is the all-duplicates case
      if (summary.duplicate_count > 0 && summary.filtered_count === 0) {
        // All contacts were duplicates - handle specially
        duplicateCount.value = summary.duplicate_count || 0;
        successCount.value = 0;
        errorCount.value = 0;
        
        // Show result immediately since no processing needed
        loading.value = false;
        // Don't show error, show warning message instead
        // Clear any existing error
        error.value = null;
        // Show warning state (we'll add this to the template)
        return;
      }
      
      // Update counters for normal completed imports
      successCount.value = summary.successful_count || 0;
      errorCount.value = summary.failed_count || 0;
      
      // Process successful imports
      if (summary.successful_imports) {
        summary.successful_imports.forEach(item => {
          const contactData = item.contact;
          importedContacts.push(contactData);
          importResults.value.push({
            status: 'success',
            name: contactData.name || contactsForBatch[item.index - 1]?.name || 'Unknown',
            contact: contactData
          });
        });
      }
      
      // Process failed imports
      if (summary.failed_imports) {
        summary.failed_imports.forEach(item => {
          importResults.value.push({
            status: 'error',
            name: item.contact_data?.name || 'Unknown',
            message: item.error || 'Failed to import contact'
          });
        });
      }
      
    } else if (batchResult.status === 'batch_completed' && batchResult.summary) {
      // Handle direct batch processing completion
      const { summary } = batchResult;
      
      // Store counts for display
      duplicateCount.value = summary.duplicate_count || 0;
      successCount.value = summary.successful_count || 0;
      errorCount.value = summary.failed_count || 0;
      
      
      // Process successful contacts
      if (batchResult.successful_contacts) {
        batchResult.successful_contacts.forEach(contact => {
          importedContacts.push(contact);
          importResults.value.push({
            status: 'success',
            name: contact.name,
            contact: contact
          });
        });
      }
      
      // Process failed contacts
      if (batchResult.failed_contacts) {
        batchResult.failed_contacts.forEach(failedContact => {
          importResults.value.push({
            status: 'error',
            name: failedContact.name,
            message: failedContact.error || 'Failed to import contact'
          });
        });
      }
      
    } else {
      // Handle unexpected response format
      errorCount.value = mappedContacts.length;
      importResults.value = mappedContacts.map(contact => ({
        status: 'error',
        name: contact.name,
        message: 'Unexpected response format from server'
      }));
    }
    
  } catch (err) {
    // Handle abort error (cancellation)
    if (err.name === 'AbortError') {
      loading.value = false;
      return;
    }
    
    // If batch fails, mark all contacts as failed
    errorCount.value = mappedContacts.length;
    importResults.value = mappedContacts.map(contact => ({
      status: 'error',
      name: contact.name,
      message: err.response?.data?.message || 'Batch import failed'
    }));
  }
  
  // Emit the imported contacts to the parent component
  emit('import-contacts', {
    contacts: importedContacts,
    results: importResults.value,
    successCount: successCount.value,
    errorCount: errorCount.value,
    duplicateCount: csvDuplicateCount
  });
  
  // Mostrar resultado da importação
  if (successCount.value > 0) {
    // Se houver pelo menos um sucesso, mostrar modal de sucesso
    
    // Capture the counts before any potential reset
    const capturedSuccessCount = successCount.value;
    const capturedErrorCount = errorCount.value;
    
    loading.value = false;
    showImportModal.value = false;
    
    // Use captured values to ensure they don't get reset
    setTimeout(() => {
      successCount.value = capturedSuccessCount;
      errorCount.value = capturedErrorCount;
      showSuccessModal.value = true;
    }, 100);
  } else if (errorCount.value > 0) {
    // Se não houver sucessos mas houver falhas, mostrar erro específico no modal atual
    loading.value = false;
    error.value = `Falha ao importar todos os ${errorCount.value} contatos. Verifique os dados e tente novamente.`;
    // Garantir que o modal permanece aberto e na tela de mapeamento
    showImportModal.value = true;
    showMapper.value = true;
    initialScreen.value = false;
  } else {
    // Caso inesperado - nenhum sucesso nem falha
    loading.value = false;
    error.value = 'Nenhum resultado de importação recebido. Tente novamente.';
    // Garantir que o modal permanece aberto e na tela de mapeamento
    showImportModal.value = true;
    showMapper.value = true;
    initialScreen.value = false;
  }
};

const backToInitialScreen = () => {
  initialScreen.value = true;
  showMapper.value = false;
  wizardStep.value = 0;
};

const closeModal = () => {
  // Cancel any ongoing import
  importCancelled.value = true;
  resetAll(); // Reset everything including counts when manually closing
  initialScreen.value = true;
  showMapper.value = false;
  wizardStep.value = 0;
  emit('close');
};

const handleSuccessModalClose = () => {
  showSuccessModal.value = false;
  emit('close');
};

// Funções para o modal de texto completo
const openFullText = (title, content) => {
  fullTextTitle.value = title;
  fullTextContent.value = content;
  showFullTextModal.value = true;
};

const closeFullText = () => {
  showFullTextModal.value = false;
  fullTextContent.value = '';
  fullTextTitle.value = '';
};

// Função para limitar o tamanho do texto com ellipsis
const getTruncatedText = (text, maxLength = 100) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Verifica se o texto precisa ser truncado
const needsTruncation = (text, maxLength = 100) => {
  return text && text.length > maxLength;
};

// Watch selectedColumn to update mapping
watch(selectedColumn, (newColumn) => {
  if (wizardStep.value === 0) {
    mapping.name = newColumn;
  } else if (wizardStep.value === 1) {
    mapping.phone_number = newColumn;
  } else if (wizardStep.value === 2) {
    mapping.email = newColumn;
  }
});

const handleColumnHover = (columnName) => {
  hoveredColumn.value = columnName;
};

const handleColumnLeave = () => {
  hoveredColumn.value = '';
};
</script>

<template>
  <!-- Modal principal -->
  <div v-if="show && showImportModal" class="fixed inset-0 z-50 flex items-center justify-center" style="background-color: rgba(0, 0, 0, 0.04); backdrop-filter: blur(4px); min-height: 100vh; width: 100vw; margin: 0; top: 0; left: 0;">
    <!-- Hidden file input -->
    <input ref="fileInput" type="file" accept=".csv" class="hidden" @change="handleFileUpload" />
    
    <!-- Container do modal -->
    <div class="relative max-h-[95vh] overflow-visible bg-n-alpha-3 shadow-md modal-container rtl:text-right skip-context-menu rounded-xl max-w-2xl w-full mx-auto">
      <!-- Tela inicial - botão para importar CSV -->
      <div v-if="initialScreen" class="flex flex-col">
        <!-- Header -->
        <div class="flex justify-between items-center px-6 py-4 border-b-2 border-n-slate-4 dark:border-n-slate-7">
          <h3 class="text-lg font-semibold text-n-slate-12">
            Importar Contatos por CSV
          </h3>
          <button class="text-n-slate-11 hover:text-n-slate-12" @click="closeModal">
            <i class="i-lucide-x text-xl"></i>
          </button>
        </div>
        
        <!-- Conteúdo da tela inicial -->
        <div class="p-6">
          <div class="text-center py-10 border-2 border-dashed border-n-slate-3 dark:border-n-slate-7 rounded-lg bg-n-slate-1 dark:bg-n-slate-3">
            <div class="flex flex-col items-center">
              <i class="i-lucide-file-spreadsheet text-5xl text-n-slate-9 mb-3"></i>
              <h3 class="text-lg font-medium text-n-slate-12 mb-3">Importar CSV</h3>
              <p class="text-sm text-n-slate-11 mb-8 px-28">
                O arquivo deve incluir colunas para nomes, números de telefone, e opcionalmente emails.
              </p>
              <Button 
                label="Selecionar um CSV" 
                variant="outline" 
                color="blue" 
                icon="i-lucide-upload"
                @click="triggerFileSelect" 
              />
            </div>
          </div>

        </div>
        
        <!-- Rodapé da tela inicial -->
        <div class="flex justify-end px-6 py-4 border-t-2 border-n-slate-4 dark:border-n-slate-7">
            <button type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="closeModal" :disabled="loading">
            Cancelar
            </button>
        </div>
      </div>

      <!-- Wizard modal for CSV mapping (exibido após selecionar arquivo) -->
      <div v-if="showMapper" class="flex flex-col">
        <!-- Header -->
        <div class="flex justify-between items-center px-6 py-4 border-b-2 border-n-slate-4 dark:border-n-slate-7">
          <h3 class="text-lg font-semibold text-n-slate-12">
            {{ wizardTitle }}
          </h3>
          <button class="text-n-slate-11 hover:text-n-slate-12" @click="closeModal">
            <i class="i-lucide-x text-xl"></i>
          </button>
        </div>

        <!-- Loading/Message body - shown when importing, showing warnings, or showing errors -->
        <div v-if="loading || showDuplicateWarning || error" class="flex flex-col px-6 py-4 justify-center">
          <div class="p-6 bg-n-slate-1 dark:bg-n-slate-3 rounded-lg border border-n-slate-3 dark:border-n-slate-7">
            <div class="space-y-4">
              <!-- Duplicate warning during loading -->
              <div v-if="loading && duplicateCount > 0" class="flex items-start">
                <i class="i-lucide-alert-triangle text-n-yellow-11 mt-1 mr-2"></i>
                <div>
                  <p class="text-base font-medium text-n-yellow-11">Contatos duplicados ignorados</p>
                  <p class="text-sm text-n-slate-11 mt-1">
                    {{ duplicateCount }} contatos foram ignorados por já existirem no sistema
                  </p>
                </div>
              </div>
              
              <!-- Importing message with spinner -->
              <div v-if="loading" class="flex items-start">
                <Spinner class="text-woot-600 mt-1 mr-2" :size="20" />
                <div>
                  <p class="text-base font-medium text-n-slate-12">Processando contatos...</p>
                  <p class="text-sm text-n-slate-11 mt-1">
                    Aguarde enquanto os contatos são processados em lotes.
                  </p>
                </div>
              </div>

              <!-- Mensagem de aviso para duplicatas (quando não está carregando) -->
              <div v-if="!loading && showDuplicateWarning" class="flex items-center justify-center">
                <div class="flex items-start">
                  <i class="i-lucide-info text-n-yellow-11 mt-1 mr-2"></i>
                  <div>
                    <p class="text-base font-medium text-n-yellow-11">Todos os contatos já existem</p>
                    <p class="text-sm text-n-slate-11 mt-1">
                      Todos os {{ duplicateCount }} contatos já existem no sistema. Nenhum contato novo foi importado.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Mensagem de erro (quando não está carregando) -->
              <div v-if="!loading && error" class="flex items-center justify-center">
                <div class="flex items-start">
                  <i class="i-lucide-alert-triangle text-n-ruby-11 mt-1 mr-2"></i>
                  <p class="text-base text-n-ruby-11">{{ error }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Content area - hidden when loading, showing warnings, or showing errors -->
        <div v-else class="flex flex-col px-6 py-4 gap-y-4">
          <!-- Progress bar -->
          <div class="flex items-center justify-between">
            <div class="w-full bg-n-slate-3 dark:bg-n-slate-6 rounded-full h-2">
              <div class="h-2 rounded-full bg-n-brand" :style="`width: ${(wizardStep + 1) * 33.3}%`">
              </div>
            </div>
          </div>

          <!-- CSV Preview Table -->
          <CustomDataTable
            v-if="parsedCsvRows.length"
            v-model:selectedColumn="selectedColumn"
            :headers="csvHeaders"
            :rows="parsedCsvRows.slice(0, 3)"
            :hover-config="{
              borderColor: '',
              backgroundColor: 'bg-n-slate-3 dark:bg-n-slate-4'
            }"
            :border-config="{
              default: 'border-n-weak dark:border-n-slate-6',
              hover: 'border-n-weak dark:border-n-slate-6',
              selected: 'border-n-weak dark:border-n-slate-6'
            }"
            :selection-config="{
              enabled: true,
              multiSelect: false,
              selectedBg: 'bg-woot-100 dark:bg-woot-800'
            }"
            :text-truncation="{
              enabled: true,
              maxLength: 50,
              showExpandButton: true
            }"
            max-height="max-h-56"
            @column-hover="handleColumnHover"
            @column-leave="handleColumnLeave"
            @expand-text="({ title, content }) => openFullText(title, content)"
          />

                      <!-- Seleção da coluna baseada no passo atual -->
          <div class="mb-6">
            <!-- Passo 0: Nome (obrigatório) -->
            <div v-if="wizardStep === 0" class="flex flex-row items-center justify-between">
              <label class="text-sm font-medium text-n-slate-12 mb-1">
                Nome <span class="text-n-ruby-11">*</span>
              </label>
              <div class="relative">
                <FilterSelect v-model="mapping.name" :options="headerOptions" variant="outline" :disabled="loading"/>
                <!-- Overlay para bloquear interações durante o carregamento -->
                <div v-if="loading" class="absolute inset-0 bg-transparent cursor-not-allowed"></div>
              </div>
            </div>

            <!-- Passo 1: Número de telefone (obrigatório) -->
            <div v-if="wizardStep === 1" class="flex flex-row items-center justify-between">
              <label class="text-sm font-medium text-n-slate-12 mb-1">
                Número de telefone <span class="text-n-ruby-11">*</span>
              </label>
              <div class="relative">
                <FilterSelect v-model="mapping.phone_number" :options="headerOptions" variant="outline" :disabled="loading"/>
                <!-- Overlay para bloquear interações durante o carregamento -->
                <div v-if="loading" class="absolute inset-0 bg-transparent cursor-not-allowed"></div>
              </div>
            </div>

            <!-- Passo 2: Email (opcional) -->
            <div v-if="wizardStep === 2" class="flex flex-row items-center justify-between">
              <label class="text-sm font-medium text-n-slate-12 mb-1">
                Email <span class="text-n-slate-9">(opcional)</span>
              </label>
              <div class="relative">
                <FilterSelect v-model="mapping.email" :options="headerOptions" variant="outline" :disabled="loading"/>
                <!-- Overlay para bloquear interações durante o carregamento -->
                <div v-if="loading" class="absolute inset-0 bg-transparent cursor-not-allowed"></div>
              </div>
            </div>
          </div>

        </div>

        <!-- Footer -->
        <div class="flex justify-between px-6 py-4 border-t-2 border-n-slate-4 dark:border-n-slate-7">
          <div>
            <Button v-if="!loading && !showDuplicateWarning && !error && wizardStep > 0" label="Voltar" variant="outline" color="slate" @click="previousWizardStep" :disabled="loading" />
            <Button v-else-if="!loading && !showDuplicateWarning && !error" label="Voltar" variant="outline" color="slate" @click="backToInitialScreen" :disabled="loading" />
          </div>

          <div class="flex flex-row gap-3">
            <button type="button"
                class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                @click="closeModal">
                Cancelar
            </button>
            <Button 
              v-if="loading || showDuplicateWarning || error"
              label="Finalizar" 
              @click="closeModal"
            />
            <Button 
              v-else
              :label="isLastStep ? 'Importar Contatos' : 'Próximo'" 
              :disabled="!canGoNext || loading" 
              :loading="loading"
              @click="nextWizardStep" 
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Full Text Modal -->
  <div v-if="showFullTextModal" class="fixed inset-0 z-[60] flex items-center justify-center" style="background-color: rgba(0, 0, 0, 0.04); backdrop-filter: blur(4px); min-height: 100vh; width: 100vw; margin: 0; top: 0; left: 0;">
    <div class="relative max-h-full overflow-auto bg-n-alpha-3 shadow-md modal-container rtl:text-right skip-context-menu rounded-xl max-w-2xl w-full mx-auto">
      <div class="flex justify-between items-center px-6 py-4 border-b-2 border-n-slate-4 dark:border-n-slate-7">
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ fullTextTitle }}
        </h3>
        <button class="text-n-slate-11 hover:text-n-slate-12" @click="closeFullText">
          <i class="i-lucide-x text-xl"></i>
        </button>
      </div>
      <div class="px-6 py-4">
        <div class="overflow-auto max-h-96 border border-n-slate-3 dark:border-n-slate-7 rounded-lg p-4 bg-n-slate-1 dark:bg-n-slate-3">
          <p class="whitespace-pre-wrap text-n-slate-12">{{ fullTextContent }}</p>
        </div>
      </div>
      <div class="flex justify-end px-6 py-4 border-t-2 border-n-slate-4 dark:border-n-slate-7">
        <Button label="Fechar" variant="outline" color="slate" @click="closeFullText" />
      </div>
    </div>
  </div>


  <!-- Success Modal -->
  <SuccessModal
    v-if="showSuccessModal"
    :show="showSuccessModal"
    :success-count="successCount"
    :customMessage="successModalMessage"
    @close="handleSuccessModalClose"
  />
</template>